import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Workflow, WorkflowOverride } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import {
  useJobSubmission,
  useConfigurationManager,
  JobSubmitPanel,
  ConfigurationPanel,
  WorkflowHeader
} from '../shared/WorkflowShared';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface WorkflowSlugRunnerProps {
  workflowId: string;
}

const WorkflowSlugRunner: React.FC<WorkflowSlugRunnerProps> = ({ workflowId }) => {
  const { user } = useAuth();
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [currentConfig, setCurrentConfig] = useState<any>({});
  const [configName, setConfigName] = useState('');
  const [jobName, setJobName] = useState('');
  const [batchSize, setBatchSize] = useState(1);
  const [loading, setLoading] = useState(true);

  // Check if user is admin
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Use shared hooks
  const { submitJob, submitting } = useJobSubmission(parseInt(workflowId));
  const {
    configurations,
    fetchConfigurations,
    saveConfiguration
  } = useConfigurationManager(parseInt(workflowId));

  useEffect(() => {
    fetchWorkflow();
    fetchConfigurations();
  }, [workflowId, fetchConfigurations]);

  const fetchWorkflow = async () => {
    try {
      const response = await axios.get(`${API_BASE}/workflows/${workflowId}`);
      const workflowData = response.data;

      // Verify this is a slug workflow
      if (workflowData.workflow_type !== 'slug') {
        alert('This is not a slug-based workflow. Redirecting...');
        window.location.href = `/workflow/${workflowId}`;
        return;
      }

      setWorkflow(workflowData);

      // Initialize parameters with default values from overrides
      // FIXED: Use override_key instead of ui_parameter_name
      const defaultConfig: any = {};
      (workflowData.workflow_overrides || []).forEach((override: WorkflowOverride) => {
        if (override.default_value !== null && override.default_value !== undefined) {
          defaultConfig[override.override_key!] = override.default_value;
        }
      });
      setCurrentConfig(defaultConfig);
    } catch (error) {
      console.error('Failed to fetch workflow:', error);
      alert('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitJob = async () => {
    // Validate required fields
    const overrides = workflow?.workflow_overrides || [];
    for (const override of overrides) {
      if (override.is_required && override.override_key) {
        const value = currentConfig[override.override_key];

        // Check if value is missing or empty
        if (value === null || value === undefined || value === '') {
          alert(`${override.display_name} is required`);
          return;
        }
      }
    }

    const result = await submitJob(jobName, currentConfig, batchSize);

    if (result.success) {
      alert(`Job submitted successfully! ${batchSize > 1 ? `Generating ${batchSize} images` : 'Generating 1 image'}`);
      window.location.href = `/job/${result.jobId}`;
    } else {
      alert(result.error || 'Failed to submit job');
    }
  };

  const handleSaveConfiguration = async () => {
    const result = await saveConfiguration(configName, currentConfig);

    if (result.success) {
      setConfigName('');
      alert('Configuration saved successfully!');
    } else {
      alert(result.error || 'Failed to save configuration');
    }
  };

  const toggleFavorite = async () => {
    if (!workflow) return;

    try {
      const response = await axios.post(`${API_BASE}/workflows/${workflow.id}/favorite`);
      setWorkflow({ ...workflow, is_favorite: response.data.is_favorite });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const loadConfiguration = (config: any) => {
    setCurrentConfig(config.parameter_values);
  };

  const updateParameter = (key: string, value: any) => {
    setCurrentConfig({ ...currentConfig, [key]: value });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Image Generation': return '🎨';
      case 'Text Processing': return '📝';
      case 'Video Generation': return '🎬';
      case 'Audio Processing': return '🎵';
      default: return '⚙️';
    }
  };

  const renderParameterInput = (override: WorkflowOverride) => {
    // FIXED: Use override_key instead of ui_parameter_name
    const paramKey = override.override_key!;
    const value = currentConfig[paramKey] || '';

    switch (override.data_type) {
      case 'integer':
        const intRules = override.validation_rules || {};
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={intRules.min}
            max={intRules.max}
            step={1}
            onChange={(e) => updateParameter(paramKey, parseInt(e.target.value) || 0)}
          />
        );

      case 'float':
        const floatRules = override.validation_rules || {};
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={floatRules.min}
            max={floatRules.max}
            step={floatRules.step || 0.1}
            onChange={(e) => updateParameter(paramKey, parseFloat(e.target.value) || 0)}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateParameter(paramKey, e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-sm text-gray-700">
              {value ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        );

      case 'select':
        const options = override.validation_rules?.options || [];
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(paramKey, e.target.value)}
          >
            <option value="">Select option...</option>
            {options.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'string':
      default:
        const stringRules = override.validation_rules || {};
        return (
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            rows={3}
            maxLength={stringRules.max_length}
            onChange={(e) => updateParameter(paramKey, e.target.value)}
            placeholder={override.description || ''}
          />
        );
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading workflow...</div>;
  }

  if (!workflow) {
    return <div className="text-center text-red-600 py-12">Workflow not found</div>;
  }

  const overrides = workflow.workflow_overrides || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getCategoryIcon(workflow.category)}</span>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">{workflow.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {workflow.category}
                </span>
                {workflow.tags && workflow.tags.length > 0 && (
                  <div className="flex gap-1">
                    {workflow.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={toggleFavorite}
            className={`p-2 rounded-full transition-colors ${
              workflow.is_favorite
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-400 hover:text-yellow-500'
            }`}
          >
            {workflow.is_favorite ? '⭐' : '☆'}
          </button>
        </div>
        <div className="flex gap-3">
          {isAdmin && (
            <a
              href={`/admin/workflow/${workflowId}/slug`}
              className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              ⚙️ Edit Configuration
            </a>
          )}
          <a href="/" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors">
            ← Back to Workflows
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Parameters</h3>
            <span className="text-sm text-gray-500 bg-blue-50 px-3 py-1 rounded-full">
              Slug Workflow: {workflow.workflow_slug}
            </span>
          </div>

          {overrides.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-lg mb-2">No parameters configured</p>
              <p className="text-sm">Contact an administrator to add parameters to this workflow</p>
            </div>
          ) : (
            overrides.map((override) => (
              <div
                key={override.id}
                className="bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {override.display_name}
                    {override.is_required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <div className="flex gap-1">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {override.data_type}
                    </span>
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                      {override.category}
                    </span>
                  </div>
                </div>
                {override.description && (
                  <p className="text-xs text-gray-500 mb-3">{override.description}</p>
                )}
                {renderParameterInput(override)}
              </div>
            ))
          )}
        </div>

        <div className="space-y-6">
          <JobSubmitPanel
            jobName={jobName}
            setJobName={setJobName}
            batchSize={batchSize}
            setBatchSize={setBatchSize}
            onSubmit={handleSubmitJob}
            submitting={submitting}
          />

          <ConfigurationPanel
            configName={configName}
            setConfigName={setConfigName}
            onSave={handleSaveConfiguration}
            configurations={configurations}
            onLoad={loadConfiguration}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkflowSlugRunner;
